import { Injectable, signal, NgZone } from '@angular/core';
import Pusher, { Channel } from 'pusher-js';
import { environment } from '../../environments/environment';
import { SyncEvent } from '../models/booth.models';

const IPAD_STORAGE_KEY = 'xo_ipad_pairing_uuid';

@Injectable({ providedIn: 'root' })
export class BoothSyncService {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private zone = new NgZone({});

  /** The booth this device is currently paired to. Required before emit()
   *  will actually send anything to the backend — but emit() also falls
   *  back to localStorage if this hasn't been set yet (see emit() below). */
  private pairingUuid: string | null = null;

  /** TV reads this signal. Updated inside NgZone so the view re-renders. */
  tvState = signal<SyncEvent>({ type: 'idle' });

  /** Fires once when this booth's channel confirms a device joined it. */
  paired = signal(false);

  /** Fires when the TV operator presses "Configure new booth" (or the
   *  iPad's own "Unsync This Device"). Both the TV and the iPad are
   *  subscribed to the same channel, so both react to this instantly. */
  resetRequested = signal(false);

  /**
   * Opens (or re-opens) a Pusher/Reverb subscription scoped to this booth's
   * pairing uuid. Used by BOTH the TV (listens for booth.sync) and the iPad
   * (listens for pairing.reset) — the iPad simply never reads `tvState`.
   */
  connect(pairingUuid: string): void {
    if (this.pairingUuid === pairingUuid && this.channel) return; // already connected
    this.disconnect();
    this.pairingUuid = pairingUuid;

    console.log(`[booth-sync] connecting to booth.${pairingUuid}`);

    this.pusher = new Pusher(environment.reverbKey, {
      wsHost:            environment.reverbHost,
      wsPort:            environment.reverbPort,
      forceTLS:          environment.reverbScheme === 'https',
      enabledTransports: ['ws', 'wss'],
      cluster:           '',
    });

    this.channel = this.pusher.subscribe(`booth.${pairingUuid}`);

    this.channel.bind('pusher:subscription_succeeded', () => {
      console.log(`[booth-sync] ✅ subscribed to booth.${pairingUuid}`);
    });
    this.channel.bind('pusher:subscription_error', (err: unknown) => {
      console.error(`[booth-sync] ❌ subscription to booth.${pairingUuid} FAILED`, err);
    });

    this.channel.bind('booth.sync', (event: SyncEvent) => {
      console.log('[booth-sync] 📥 received booth.sync', event);
      this.zone.run(() => this.tvState.set(event));
    });

    this.channel.bind('pairing.joined', () => {
      console.log('[booth-sync] 📥 received pairing.joined');
      this.zone.run(() => this.paired.set(true));
    });

    this.channel.bind('pairing.reset', () => {
      console.log('[booth-sync] 📥 received pairing.reset');
      this.zone.run(() => this.resetRequested.set(true));
    });
  }

  /**
   * iPad calls this to push a state update to the TV. Emitting only needs
   * the pairing uuid to attach to the outbound POST body — it does NOT
   * require connect() to have already run (that's only needed to LISTEN
   * for inbound events like pairing.reset).
   *
   * Falls back to reading the uuid straight from localStorage when it
   * isn't set in memory yet, to avoid a race with AppComponent's async
   * pairing restore on a fresh page load (e.g. an iOS deeplink return
   * landing directly on /stand/result?guest_id=...).
   */
  emit(event: SyncEvent): void {
    const uuid = this.pairingUuid ?? localStorage.getItem(IPAD_STORAGE_KEY);
    if (!uuid) {
      console.warn('[booth-sync] ❌ emit() called with no pairing_uuid available — ignoring', event);
      return;
    }
    console.log(`[booth-sync] 📤 emitting to booth.${uuid}`, event);
    fetch(`${environment.apiUrl}/stand/booth-sync`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ ...event, pairing_uuid: uuid }),
    })
      .then(res => res.json())
      .then(json => console.log('[booth-sync] 📤 emit response', json))
      .catch(err => console.error('[booth-sync] ❌ emit failed', err));
  }

  resetSignals(): void {
    this.paired.set(false);
    this.resetRequested.set(false);
  }

  disconnect(): void {
    if (this.channel) { this.channel.unbind_all(); this.channel = null; }
    if (this.pusher)  { this.pusher.disconnect(); this.pusher = null; }
    this.pairingUuid = null;
  }
}
